import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { apiService } from "@/lib/api-functions/api-service";
import { EXPORT_VERSION } from "./constants";
import { generateCleanFilename, downloadBlob } from "./utils";

interface ExportData {
  metadata: {
    gameId: string;
    gameName: string;
    exportDate: string;
    exportVersion: string;
  };
  objects: unknown[];
  templates: unknown[];
  texts: unknown[];
  documents: unknown[];
  relationships: unknown[];
  tags: unknown[];
  types: unknown[];
}

interface Game {
  id: string;
  name: string;
}

export function useCompleteExport() {
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState("");

  const handleExportAll = async (selectedGame: Game) => {
    setIsExporting(true);
    setExportProgress("Starting export...");

    try {
      const exportData: ExportData = {
        metadata: {
          gameId: selectedGame.id,
          gameName: selectedGame.name,
          exportDate: new Date().toISOString(),
          exportVersion: EXPORT_VERSION,
        },
        objects: [],
        templates: [],
        texts: [],
        documents: [],
        relationships: [],
        tags: [],
        types: [],
      };

      // 1. Fetch Objects
      setExportProgress("Fetching objects...");
      const glossaryResult = await apiService.fetchObjects(selectedGame.id);
      if (glossaryResult.success && glossaryResult.data) {
        exportData.objects = glossaryResult.data;
        toast({
          title: "✓ Objects exported",
          description: `${glossaryResult.data.length} objects collected`,
        });
      }

      // 2. Fetch Templates
      setExportProgress("Fetching templates...");
      const blueprintsResult = await apiService.fetchBlueprints(selectedGame.id);
      if (blueprintsResult.success && blueprintsResult.data) {
        exportData.templates = blueprintsResult.data;
        toast({
          title: "✓ Templates exported",
          description: `${blueprintsResult.data.length} templates collected`,
        });
      }

      // 3. Fetch Texts
      setExportProgress("Fetching texts...");
      const stringsResult = await apiService.fetchStringsItems(selectedGame.id);
      if (stringsResult.success && stringsResult.data) {
        exportData.texts = stringsResult.data;
        toast({
          title: "✓ Texts exported",
          description: `${stringsResult.data.length} text items collected`,
        });
      }

      // 4. Fetch Documents
      setExportProgress("Fetching documents...");
      const documentsResult = await apiService.fetchDocumentItems(selectedGame.id);
      if (documentsResult.success && documentsResult.data) {
        exportData.documents = documentsResult.data;
        toast({
          title: "✓ Documents exported",
          description: `${documentsResult.data.length} documents collected`,
        });
      }

      // 5. Fetch Relationships
      setExportProgress("Fetching relationships...");
      try {
        const allRelationships: unknown[] = [];
        
        // Get relationships for all objects
        for (const obj of exportData.objects as Array<{ id: string }>) {
          const relResult = await apiService.getEntityRelationships(
            obj.id,
            'glossary',
            selectedGame.id
          );
          if (relResult.success && relResult.data) {
            allRelationships.push({
              entity_id: obj.id,
              entity_type: 'glossary',
              ...relResult.data,
            });
          }
        }
        
        // Get relationships for all documents
        for (const doc of exportData.documents as Array<{ id: string }>) {
          const relResult = await apiService.getEntityRelationships(
            doc.id,
            'document',
            selectedGame.id
          );
          if (relResult.success && relResult.data) {
            allRelationships.push({
              entity_id: doc.id,
              entity_type: 'document',
              ...relResult.data,
            });
          }
        }

        exportData.relationships = allRelationships;
        toast({
          title: "✓ Relationships exported",
          description: `${allRelationships.length} relationship sets collected`,
        });
      } catch (err) {
        console.error("Error fetching relationships:", err);
      }

      // 6. Fetch Tags
      setExportProgress("Fetching tags...");
      const tagsResult = await apiService.fetchTags(selectedGame.id);
      if (tagsResult.success && tagsResult.data) {
        exportData.tags = tagsResult.data;
      }

      // 7. Fetch Types
      setExportProgress("Fetching types...");
      const typesResult = await apiService.fetchTypes(selectedGame.id);
      if (typesResult.success && typesResult.data) {
        exportData.types = typesResult.data;
      }

      // Download
      setExportProgress("Preparing download...");
      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: "application/json" });
      const filename = generateCleanFilename(selectedGame.name, 'export');
      
      downloadBlob(blob, `${filename}.json`);

      toast({
        title: "Export Complete!",
        description: "Your game data has been exported successfully.",
        variant: "default",
      });

    } catch (error) {
      console.error("Error exporting data:", error);
      toast({
        title: "Export Failed",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
      setExportProgress("");
    }
  };

  return {
    isExporting,
    exportProgress,
    handleExportAll
  };
}
